import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, ChevronRight, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["ALL", "PENDING", "ACTIVE", "SUSPENDED", "EXPIRED"];
const TYPE_OPTIONS = ["ALL", "MEMBER", "TEST", "REVIEW"];

const statusColor = (s) => {
  if (s === "ACTIVE") return "text-lv-active";
  if (s === "SUSPENDED" || s === "EXPIRED") return "text-lv-inactive";
  if (s === "PENDING") return "text-bronze";
  return "text-lv-muted";
};

const typeBadge = (t) => {
  if (t === "TEST") return "bg-amber-500/15 text-amber-400 border-amber-500/30";
  if (t === "REVIEW") return "bg-purple-500/15 text-purple-400 border-purple-500/30";
  return "bg-lv-active/10 text-lv-active border-lv-active/20";
};

export default function MemberList() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [showTestForm, setShowTestForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const qc = useQueryClient();

  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (statusFilter && statusFilter !== "ALL") params.set("status", statusFilter);
  if (typeFilter && typeFilter !== "ALL") params.set("member_type", typeFilter);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["admin-members", search, statusFilter, typeFilter],
    queryFn: () => api.get(`/admin/members?${params.toString()}`),
    keepPreviousData: true,
  });

  const counts = {
    total: members.length,
    member: members.filter((m) => m.member_type === "MEMBER").length,
    test: members.filter((m) => m.member_type === "TEST").length,
    review: members.filter((m) => m.member_type === "REVIEW").length,
  };

  return (
    <div data-testid="admin-member-list">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide">Members</h1>
          <p className="text-sm text-lv-muted tracking-wide mt-1">
            {counts.total} total
            {counts.test > 0 && <span className="text-amber-400/70 ml-2">{counts.test} test</span>}
            {counts.review > 0 && <span className="text-purple-400/70 ml-2">{counts.review} review</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowTestForm(true)} className="lv-btn-secondary inline-flex items-center gap-2 min-h-[44px] text-xs" data-testid="admin-create-test-member-btn">
            <Plus size={14} /> Test Member
          </button>
          <Link to="/admin/members/new" className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="admin-create-member-btn">
            <Plus size={16} /> New Member
          </Link>
        </div>
      </div>

      {showTestForm && <TestMemberForm onClose={() => setShowTestForm(false)} onCreated={() => { qc.invalidateQueries({ queryKey: ["admin-members"] }); setShowTestForm(false); }} />}

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-lv-muted pointer-events-none" />
          <input type="text" inputMode="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or ID..." className="lv-input !pl-11" data-testid="admin-member-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[160px] bg-walnut-deep border-lv-border text-cream min-h-[44px]" data-testid="admin-member-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-walnut-deep border-lv-border">
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[160px] bg-walnut-deep border-lv-border text-cream min-h-[44px]" data-testid="admin-member-type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-walnut-deep border-lv-border">
            {TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-lv-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-lv-border hover:bg-transparent">
              <TableHead className="text-lv-muted text-[10px] tracking-[0.2em] uppercase">ID</TableHead>
              <TableHead className="text-lv-muted text-[10px] tracking-[0.2em] uppercase">Name</TableHead>
              <TableHead className="text-lv-muted text-[10px] tracking-[0.2em] uppercase hidden md:table-cell">Chapter</TableHead>
              <TableHead className="text-lv-muted text-[10px] tracking-[0.2em] uppercase">Type</TableHead>
              <TableHead className="text-lv-muted text-[10px] tracking-[0.2em] uppercase">Status</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-lv-muted py-12">Loading...</TableCell></TableRow>
            ) : members.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-lv-muted py-12" data-testid="admin-no-members">No members found</TableCell></TableRow>
            ) : members.map((m) => (
              <TableRow key={m.id} className="border-lv-border hover:bg-walnut/50 transition-colors">
                <TableCell className="font-mono text-sm text-cream" data-testid={`member-code-${m.member_code}`}>{m.member_code}</TableCell>
                <TableCell className="text-cream text-sm">{m.full_name}</TableCell>
                <TableCell className="text-cream text-sm hidden md:table-cell">{m.home_chapter_name || "\u2014"}</TableCell>
                <TableCell>
                  <span className={`text-[9px] tracking-[0.15em] uppercase font-medium px-2 py-0.5 border inline-block ${typeBadge(m.member_type)}`} data-testid={`member-type-${m.member_code}`}>
                    {m.member_type}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`text-xs tracking-[0.2em] uppercase font-medium ${statusColor(m.status)}`}>{m.status}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {m.member_type === "TEST" && (
                      <button onClick={(e) => { e.preventDefault(); setDeleteTarget(m); }} className="text-lv-inactive/60 hover:text-lv-inactive transition-colors min-w-[32px] min-h-[32px] inline-flex items-center justify-center" data-testid={`delete-test-${m.member_code}`} title="Delete test member">
                        <Trash2 size={14} />
                      </button>
                    )}
                    <Link to={`/admin/members/${m.id}`} className="text-lv-muted hover:text-bronze transition-colors min-w-[44px] min-h-[44px] inline-flex items-center justify-center" data-testid={`member-detail-${m.member_code}`}>
                      <ChevronRight size={16} />
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {deleteTarget && <DeleteTestModal member={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={() => { qc.invalidateQueries({ queryKey: ["admin-members"] }); setDeleteTarget(null); }} />}
    </div>
  );
}

function TestMemberForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ full_name: "", preferred_name: "", email: "", home_chapter_id: "" });
  const [saving, setSaving] = useState(false);
  const { data: chapters = [] } = useQuery({ queryKey: ["chapters"], queryFn: () => api.get("/chapters") });

  const submit = async () => {
    if (!form.full_name.trim() || !form.email.trim() || !form.home_chapter_id) {
      toast.error("Name, email, and chapter are required"); return;
    }
    setSaving(true);
    try {
      const res = await api.post("/admin/test-members", form);
      toast.success(`Test member ${res.member_code} created`);
      onCreated();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="bg-walnut border border-amber-500/30 p-5 mb-6 space-y-3" data-testid="test-member-form">
      <h3 className="text-xs tracking-[0.3em] uppercase text-amber-400 mb-2">Create Test Member</h3>
      <p className="text-xs text-lv-muted">Test members use TEST-XXX numbering and do not consume LV numbers.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Full Name *</label><input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="lv-input" data-testid="test-member-name" /></div>
        <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Preferred Name</label><input value={form.preferred_name} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} className="lv-input" /></div>
        <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Email *</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="lv-input" data-testid="test-member-email" /></div>
        <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Chapter *</label>
          <Select value={form.home_chapter_id} onValueChange={(v) => setForm({ ...form, home_chapter_id: v })}>
            <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]" data-testid="test-member-chapter"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-walnut-deep border-lv-border">{chapters.map((ch) => <SelectItem key={ch.id} value={ch.id} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{ch.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={submit} disabled={saving} className="lv-btn-primary text-xs min-h-[36px] px-4" data-testid="test-member-submit">{saving ? "Creating..." : "Create Test Member"}</button>
        <button onClick={onClose} className="lv-btn-secondary text-xs min-h-[36px] px-4">Cancel</button>
      </div>
    </div>
  );
}

function DeleteTestModal({ member, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const confirmed = confirmText === member.member_code;

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/test-members/${member.id}`);
      toast.success(`${member.member_code} deleted`);
      onDeleted();
    } catch (err) { toast.error(err.message); } finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-walnut border border-lv-inactive/40 p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="delete-test-modal">
        <div className="flex items-center gap-3 text-lv-inactive">
          <AlertTriangle size={20} />
          <h3 className="text-sm font-medium tracking-wide">Delete Test Member</h3>
        </div>
        <p className="text-xs text-lv-muted">
          This will permanently delete <span className="text-cream font-medium">{member.full_name}</span> ({member.member_code}) and all associated records.
        </p>
        <p className="text-xs text-lv-muted">
          Type <span className="text-cream font-mono">{member.member_code}</span> to confirm:
        </p>
        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="lv-input font-mono" placeholder={member.member_code} data-testid="delete-confirm-input" />
        <div className="flex gap-2">
          <button onClick={doDelete} disabled={!confirmed || deleting} className="bg-lv-inactive/20 border border-lv-inactive/40 text-lv-inactive hover:bg-lv-inactive/30 disabled:opacity-40 disabled:cursor-not-allowed text-xs px-4 min-h-[36px] transition-colors" data-testid="delete-confirm-btn">
            {deleting ? "Deleting..." : "Delete Permanently"}
          </button>
          <button onClick={onClose} className="lv-btn-secondary text-xs min-h-[36px] px-4">Cancel</button>
        </div>
      </div>
    </div>
  );
}
